import { Box, Icon, TextLink } from '@palmetto/palmetto-components';

import { MilestoneType, ProgramType } from 'types';

export const DesignDocsDescription = (
  <Box fontSize="sm" childGap="xs" color="body-secondary">
    <p>Upload all required design documents:</p>
    <Box childGap="2xs" as="ul">
      <li>Plan Set</li>
      <li>Production Model</li>
      <li>Shade Report</li>
    </Box>
  </Box>
);

export const NewCommunityDesignDetailsDescription = (
  <Box fontSize="sm" childGap="xs" color="body-secondary">
    <p>Upload design details:</p>
    <Box childGap="2xs" as="ul">
      <li>Plan Set</li>
      <li>
        Production Model <b>(optional)</b>
      </li>
      <li>
        Shade Report <b>(optional)</b>
      </li>
    </Box>
  </Box>
);

export const PermitDocsDescription = (
  <Box fontSize="sm" childGap="2xs">
    <Box>Upload all required permit documents:</Box>
    <Box childGap="2xs" as="ul">
      <li>Approved Permit</li>
    </Box>
  </Box>
);

export const IncentivesDescription = () => (
  <Box fontSize="sm" childGap="xs" color="body-secondary">
    <p>Upload the required incentive documentation:</p>
    <Box childGap="2xs" as="ul">
      <li>Applicable Incentive Documents</li>
    </Box>
    <TextLink
      href="https://help.palmetto.finance/en/articles/9083905-incentive-deliverables-m1-milestone"
      target="_blank"
      rel="noreferrer"
    >
      See incentive requirements
    </TextLink>
  </Box>
);

export const MarketSpecificRequirementsDescription = (milestoneType: MilestoneType) => (
  <Box fontSize="sm" childGap="xs" color="body-secondary">
    <p>Upload any documents that are required in the given market. These can include:</p>
    <Box childGap="2xs" as="ul">
      <li>Incentives</li>
      <li>Disclosures</li>
      <li>CSLB Disclosure (CA Only)</li>
      <li>Interconnection Agreements</li>
    </Box>
    <p>
      Check the{' '}
      <TextLink
        href={
          milestoneType === MilestoneType.install
            ? 'https://help.palmetto.finance/en/articles/9083905-incentive-deliverables-m1-milestone'
            : 'https://help.palmetto.finance/en/articles/9085138-incentive-deliverables-m2-milestone'
        }
        target="_blank"
        rel="noreferrer"
      >
        Knowledge Base
      </TextLink>{' '}
      for full requirements in each market.
    </p>
  </Box>
);

export const ContractDocumentSectionDescription = () => (
  <Box fontSize="sm" childGap="xs" color="body-secondary">
    <p>Active contract between homeowner and LightReach</p>
  </Box>
);

export const UtilityBillDescription = ({
  showUtilityBill,
  isUtilityBillApprovedDuringNtp,
}: {
  showUtilityBill: boolean;
  isUtilityBillApprovedDuringNtp?: boolean;
}) => {
  let text = '';
  if (isUtilityBillApprovedDuringNtp) {
    text = 'The Utility Bill for this account was approved during the NTP Milestone';
  } else if (showUtilityBill) {
    text = 'Upload recent utility bill.';
  }

  return (
    <Box fontSize="sm" childGap="xs" color="body-secondary">
      <p>{text} </p>
    </Box>
  );
};

export const InterconnectionAgreementDescription = () => {
  return (
    <Box fontSize="sm" childGap="xs" color="body-secondary">
      <p>Upload the approved interconnection agreement (CA only)</p>
    </Box>
  );
};

export const InstallPhotosDescription = (
  <Box childGap="xs">
    <p>Upload photos of the installation to all applicable categories.</p>
  </Box>
);

/*
TODO: Remove PvSystemDescription once the flagDrivenPackageReview is true in prod,
because it's being replaced by SiteEligibilitySectionDescription
*/
export const PvSystemDescription = (
  <TextLink
    href="https://help.palmetto.finance/en/articles/12524213-avl-all-things-hardware"
    rel="noreferrer"
    target="_blank"
  >
    Confirm equipment details
  </TextLink>
);

export const InstallerDescription = <p>Required by the project location state</p>;

export const AddersDescription = <Box>Adders included in the project</Box>;

export const EquipmentDetailsDescription = (isOptionalForNtp?: boolean) => (
  <Box color="body-secondary">
    <p>
      <Box
        as="a"
        direction="row"
        alignItems="center"
        childGap="xs"
        href="https://help.palmetto.finance/en/articles/12524213-avl-all-things-hardware"
        target="_blank"
        rel="noreferrer"
      >
        <span>Approved Vendor List Reference</span>
        <Icon name="launch-app" size="xs" color="body-tertiary" />
      </Box>
    </p>
    {isOptionalForNtp && <p>Optional for NTP</p>}
  </Box>
);

export const ActivationDocumentsDescription = (
  <Box color="body-secondary" fontSize="sm" childGap="2xs">
    <Box as="p">Upload all required incentives documents:</Box>
    <Box childGap="2xs" as="ul">
      <li>Extended Warranty</li>
      <li>Incentives</li>
    </Box>
  </Box>
);

export const ContractKwhInfoDescription = (programType: ProgramType) => (
  <Box color="body-secondary" fontSize="sm" childGap="2xs" margin="sm 0 0 0">
    <Box as="p">
      Install Year-1 production estimate must be within -5 - {programType === ProgramType.doePr ? '20%' : '10%'}{' '}
      difference from year-1 production estimate in the contract
    </Box>
  </Box>
);

export const PermitDescription = (
  <Box as="p" color="body-secondary" fontSize="sm">
    Copy of approved permit (photo of physical document or AHJ portal screenshot)
  </Box>
);

export const SystemDetailsAndSiteQualificationSectionDescription = () => (
  <Box fontSize="sm" childGap="xs" color="body-secondary">
    <p>Please confirm all equipment details match the installed system.</p>
    <p>
      Any issues found by LightReach as they pertain to the eligibility of the home or property will be added as
      flags here during the QC Review Process.
    </p>
    <TextLink
      href="https://help.palmetto.finance/en/articles/12524213-avl-all-things-hardware"
      rel="noreferrer"
      target="_blank"
    >
      Confirm equipment details
    </TextLink>
    <TextLink
      href="https://help.palmetto.finance/en/articles/8708580-eligible-sites"
      rel="noreferrer"
      target="_blank"
    >
      Eligible site details
    </TextLink>
  </Box>
);
